import { Helmet } from "react-helmet-async"
import { buildMeta } from "../lib/seo"

// Site origin — used to build absolute canonical/JSON-LD URLs. Canonicals must
// always point at the public domain: during build-time prerendering the app runs on
// http://127.0.0.1 (and in dev on localhost), so trusting window.location.origin
// would bake wrong absolute URLs into the static snapshots. Only use the runtime
// origin when it is actually the production site.
const runtimeOrigin = typeof window !== "undefined" ? window.location?.origin : null
export const siteBase =
  runtimeOrigin && /(^|\.)tanaka\.co\.za$/.test(new URL(runtimeOrigin).hostname)
    ? runtimeOrigin
    : "https://tanaka.co.za"

// Default social-preview image (must be an existing file in public/).
export const defaultOgImage = `${siteBase}/logo.png`

/**
 * Renders document <head> SEO for a page: title, meta (description/OG/Twitter),
 * canonical link, optional keywords, and any number of JSON-LD blocks.
 *
 * Props: title, description, canonical, ogImage, ogType, keywords, jsonLd, noindex
 *   - jsonLd: a single schema object or an array of them.
 *   - noindex: keep the page out of search results. Use on private/transactional
 *     routes (checkout, wallet, account, receipts) and on the 404 page — a static SPA
 *     answers unknown URLs with HTTP 200, so noindex is what prevents soft-404 indexing.
 *     "follow" is kept so link equity still flows through the page.
 */
const Seo = ({ title, description, canonical, ogImage, ogType = "website", keywords, jsonLd, noindex = false }) => {
  const meta = buildMeta({ title, description, canonical, ogImage: ogImage || defaultOgImage, ogType })
  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []

  return (
    <Helmet>
      <title>{meta.title}</title>
      {/* Always emitted so Helmet owns the robots directive: react-helmet-async dedupes
          by meta name and the innermost <Seo> wins, so a page-level noindex cleanly
          replaces the app-level default. (The static tag was removed from index.html —
          Helmet cannot override tags it does not manage, which would leave a page with
          both "index, follow" and "noindex, follow".) */}
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      {meta.meta.map((m, i) => (
        <meta key={i} {...m} />
      ))}
      {meta.link.map((l, i) => (
        <link key={i} {...l} />
      ))}
      {keywords && <meta name="keywords" content={keywords} />}
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  )
}

export default Seo
